import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import L, { LatLngTuple } from 'leaflet';
import { ResponseDataGet } from '../../../../models/responseDataGet';
import { EntTerritorialsUnits } from '../../../../models/handBooks/territorialsUnits';
import 'leaflet/dist/leaflet.css';
import styles from './style.module.css';
import { Select } from 'antd';
import { IPesticidesResponse, IPesticidesItem } from '../api/types';
import LeasingSelectYear from '../../../mechanization/leasing/Leasingselect';
import { PreloaderSvg } from '../../../preloaderSvg/PreloaderSvg';

interface PesticidesMapProps {
  latitude: number;
  longitude: number;
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  selectedYear: number | null;
  onYearChange: (value: number | null) => void;
  isLoadingFertilizers: boolean;
  isLoadingFertilizersOnMap: boolean;
  onRegionClick: (regionId: number, regionName: string) => void;
  onResetRegion: () => void;
  selectedRegionName: string;
  pesticidesData?: IPesticidesResponse;
  initialRegionId?: number;
}

const MapComponent: React.FC<{ 
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  onRegionClick: (regionId: number, regionName: string) => void;
  initialRegionId?: number;
}> = ({ polygonsData, onRegionClick, initialRegionId }) => {
  const map = useMap();
  const polygonLayersRef = useRef<{[key: number]: L.Polygon}>({});
  const activePolygonRef = useRef<L.Polygon | null>(null);

  const setActivePolygonStyle = useCallback((polygonLayer: L.Polygon) => {
    if (activePolygonRef.current !== polygonLayer) {
      // Сброс стилей всех полигонов
      Object.values(polygonLayersRef.current).forEach(layer => {
        layer.setStyle({
          fillOpacity: 0.1,
          weight: 1,
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)'
        });
      });

      // Установка стилей активного полигона
      polygonLayer.setStyle({
        fillOpacity: 0.3,
        weight: 2,
        color: 'green',
        fillColor: 'green'
      });
      activePolygonRef.current = polygonLayer;
    }
  }, []);

  useEffect(() => {
    if (!map || !polygonsData?.res) return;

    const polygonsArray = Array.isArray(polygonsData.res) ? polygonsData.res : [polygonsData.res];

    Object.values(polygonLayersRef.current).forEach(layer => layer.remove());
    polygonLayersRef.current = {};

    polygonsArray.forEach((polygon: EntTerritorialsUnits) => {
      if (!polygon.id || !polygon.nameRu || !polygon.polygonJson) return;

      try {
        const parsedPolygon = JSON.parse(polygon.polygonJson);
        if (!parsedPolygon || !Array.isArray(parsedPolygon) || !parsedPolygon[0]) return;

        const geoJson = parsedPolygon[0].geojson;
        if (!geoJson || !geoJson.coordinates || !Array.isArray(geoJson.coordinates)) return;

        const coordinates = geoJson.type === 'MultiPolygon' 
          ? geoJson.coordinates[0][0]  
          : geoJson.coordinates[0];    

        if (!Array.isArray(coordinates)) return;

        const latLngs: [number, number][] = coordinates.map((coord: number[]) => [coord[1], coord[0]]);

        const polygonLayer = L.polygon(latLngs, {
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)',
          weight: 1,
          fillOpacity: 0.1
        }).addTo(map);

        polygonLayer.on('click', () => {
          if (polygon.id) {
            setActivePolygonStyle(polygonLayer);
            onRegionClick(polygon.id, polygon.nameRu!);
          }
        });

        polygonLayersRef.current[polygon.id] = polygonLayer;
        
        // Установка начального активного полигона
        if (initialRegionId && polygon.id === initialRegionId) {
          setActivePolygonStyle(polygonLayer);
        }
      } catch (error) {
        console.error('Error parsing polygon:', error);
      }
    });

    return () => {
      Object.values(polygonLayersRef.current).forEach(layer => layer.remove());
    };
  }, [map, polygonsData, onRegionClick, setActivePolygonStyle, initialRegionId]);

  return null;
};

export const PesticidesMap: React.FC<PesticidesMapProps> = ({ 
  latitude, 
  longitude, 
  polygonsData,
  selectedYear,
  onYearChange,
  isLoadingFertilizers,
  isLoadingFertilizersOnMap,
  onRegionClick,
  onResetRegion,
  selectedRegionName,
  pesticidesData,
  initialRegionId
}) => {
  const center: LatLngTuple = [latitude, longitude];
  const [searchText, setSearchText] = useState('');

  const getDistrictOptions = () => {
    if (!pesticidesData?.res) return [];

    return pesticidesData.res
      .map(item => ({
        value: item.pesticidesNameRu,
        label: item.pesticidesNameRu
      }))
      .filter((item, index, self) => 
        index === self.findIndex(t => t.value === item.value)
      );
  };

  const renderTableData = () => {
    if (!pesticidesData?.res) return null;

    const searchValue = searchText?.toLowerCase() || '';
    const filteredItems = pesticidesData.res.filter(item =>
      item.pesticidesNameRu.toLowerCase().includes(searchValue)
    );

    if (filteredItems.length === 0) {
      return (
        <div className={styles.noData}>
          Нет данных
        </div>
      );
    }

    return (
      <div className={styles.statsInfo}>
        {filteredItems.map((item, index) => (
          <div key={index} className={styles.statsSection}>
            <div className={styles.cropInfo}>
              <span className={styles.cropName}>{item.pesticidesNameRu}</span>
              <span className={styles.percentage}>{(item.supplyPercent).toFixed(1)}%</span>
            </div>
            <div className={styles.areaInfo}>
              <span className={styles.areaLabel}>Необходимо:</span>
              <span className={styles.areaValue}>{item.neededTonnes}</span>
            </div>
            <div className={styles.areaInfo}>
              <span className={styles.areaLabel}>Внесено:</span>
              <span className={styles.areaValue}>{item.suppliedTonnes}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.mapContainer}>
      <div className={styles.mapWrapper}>
        <div className={styles.yearSelector}>
          <LeasingSelectYear
            value={selectedYear}
            onChange={onYearChange}
          />
        </div>
        <MapContainer
          className={styles.map}
          center={center}
          zoom={6}
          minZoom={6}
          scrollWheelZoom={true}
          zoomControl={false}
        >
      <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
          <ZoomControl position="bottomright" />
          <MapComponent polygonsData={polygonsData} onRegionClick={onRegionClick} initialRegionId={initialRegionId} />
        </MapContainer>
      </div>

      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          {selectedRegionName ? (
            <div className={styles.selectedRegion}>
              <h3>{selectedRegionName}</h3>
              <button onClick={onResetRegion} className={styles.resetButton}>
                ×
              </button>
            </div>
          ) : (
            <h3>Данные по республике</h3>
          )}
        </div>

        <Select
          showSearch
          placeholder="Поиск пестицида"
          optionFilterProp="children"
          onChange={(value) => setSearchText(value)}
          className={styles.searchInput}
          options={getDistrictOptions()}
          allowClear
        />

        {isLoadingFertilizers ? (
          <div className={styles.loader}>
            <PreloaderSvg />
          </div>
        ) : (
          renderTableData()
        )}
      </div>
    </div>
  );
};
